import styles from './EnvironmentOverlay.module.scss'

/** Where pbp-api's feature stacks serve the apps: `<app>.<feature>.dev.goodstack.io`. */
const FEATURE_STACK_DOMAIN = '.dev.goodstack.io'

/**
 * English only, on purpose: it is for developers on a development host, not for users, and
 * it has to show in every app - several of which have no translations to put it in.
 */
const featureStackWarning = (feature: string) =>
  `DEVELOPMENT · ${feature} feature stack · not a real Goodstack site - never enter production logins or real data`

/** The feature a feature stack serves this page for, or undefined anywhere else. */
export const featureStackName = (hostname: string): string | undefined =>
  hostname.endsWith(FEATURE_STACK_DOMAIN)
    ? hostname.slice(0, -FEATURE_STACK_DOMAIN.length).split('.').pop()
    : undefined

export type EnvironmentOverlayProps = {
  /**
   * The app's environment, as its config names it (`NX_PUBLIC_REACT_APP_ENV`). Anywhere but
   * production it gets a tab. Omitted, there is no tab.
   */
  environment?: string
  /**
   * What the tab says, in the app's own words and translations - "Staging environment", say.
   * Omitted, it shows the environment's name.
   */
  label?: string
  /** `fixed` keeps the tab on screen; `absolute` lets it scroll away with the page. */
  position?: 'fixed' | 'absolute'
}

/**
 * Which environment this is, shared by every app.
 *
 * On a pbp-api feature stack it is loud: a red bar naming the feature and saying it is not
 * a real Goodstack site, since a goodstack.io name over plain http - often served from a
 * sandbox an AI agent works in - is otherwise easy to take for one and give a production
 * login or real data. Not in a browser driven by test automation, where it would only get
 * in the way of e2e locators and screenshots, and nobody types a password. Elsewhere it is
 * the app's environment tab, outside production.
 */
export function EnvironmentOverlay({
  environment,
  label,
  position = 'fixed',
}: EnvironmentOverlayProps) {
  const feature = featureStackName(window.location.hostname)

  if (feature && !navigator.webdriver) {
    return (
      <div
        role="status"
        data-testid="feature-stack-banner"
        className={styles['featureStackBar']}
      >
        {featureStackWarning(feature)}
      </div>
    )
  }

  // Case-insensitive, so a misspelt config never puts a tab on production.
  if (!environment || environment.toLowerCase() === 'production') {
    return null
  }

  return (
    // The one style the app chooses, so it stays beside the prop that sets it.
    <div
      data-testid="environment-overlay"
      className={styles['tab']}
      style={{ position }}
    >
      <div className={styles['strip']} />
      <p className={styles['message']}>{label ?? environment}</p>
    </div>
  )
}
